import React from 'react';
import { useLocation } from 'react-router';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import { FormNavigatebtn } from '../../../components/Button/Buttons';
import { FormSubmitButton } from '../../../components/Button/FormSubmitButton';
import { FormComponent } from '../../../components/FormCom/FormComponent';
import OTPInputField from '../../../components/FormCom/OtpInputField';
import useResendSignupVerificationOtp from '../../../hooks/auth/admin/useResendSignupVerificationOtp';
import useSignupVerificationOtp from '../../../hooks/auth/admin/useSignupVerificationOtp';
import {
  VerifySignUpOtpDataType,
  verifySignUpOtpInitialValues,
  verifySignUpOtpSchema,
} from '../../../models/auth/verifySignUpOtp.model';

const SignupVerification: React.FC = () => {
  const location = useLocation();
  const navigationData = location.state?.data;

  const { signupVerificationOtp, loading, otpErrorBanner } =
    useSignupVerificationOtp();

  const { loadingResendPasswd, resendSignupVerificationOtp } =
    useResendSignupVerificationOtp();

  const onSubmit = (value: VerifySignUpOtpDataType) => {
    signupVerificationOtp({ ...value, email: navigationData?.email });
  };

  const onResendVerification = (e: any) => {
    e.preventDefault();
    resendSignupVerificationOtp(navigationData?.email);
  };

  return (
    <Container>
      <FormWrapper>
        <FormHeader>
          <div className='title'>Verify your Account</div>
          <div className='description'>
            A one-time password has been sent to your email <br />
            {navigationData?.email ?? 'no email provided'}
          </div>
        </FormHeader>
        {otpErrorBanner && <InvalidOTP>Invalid OTP</InvalidOTP>}
        <FormComponent
          initialValues={verifySignUpOtpInitialValues}
          schema={verifySignUpOtpSchema}
          onSubmit={onSubmit}
        >
          <OTPInputField
            label=''
            name='otp'
            className={!otpErrorBanner ? 'rejectedOtp' : ''}
          />
          <FormSendButtonArea>
            Didn't get the code?
            <FormNavigatebtn onClick={onResendVerification}>
              Resend Otp {loadingResendPasswd && <LoadingOutlined />}
            </FormNavigatebtn>
          </FormSendButtonArea>
          <FormSubmitButton loading={loading} className='topMargin'>
            Verify
          </FormSubmitButton>
        </FormComponent>
      </FormWrapper>
    </Container>
  );
};

export default SignupVerification;

const Container = styled.div`
  display: flex;
  height: 100vh;
  color: ${({ theme }) => theme.palette.grayTextColor};
  background-color: white;
  /* background-color: ${({ theme }) => theme.palette.pageBgColor}; */
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 400px;
  align-self: center;
  gap: 24px;
  margin: auto;

  @media (max-width: 480px) {
    width: 100%;
    max-width: 400px;
    padding: 0 20px;
  }
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  margin-bottom: 4px;

  & > .title {
    font-weight: 600;
    font-size: 20px;
    line-height: 27px;
    color: ${({ theme }) => theme.palette.logirateBlack};
  }

  & > .description {
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
  }
`;

const InvalidOTP = styled.div`
  display: flex;
  text-align: center;
  padding: 16px 108px;
  background-color: ${({ theme }) => theme.palette.warningBgColor};
  color: ${({ theme }) => theme.palette.warningTextColor};
  border-radius: 8px;
  font-weight: 500;
  line-height: 16px;
  font-size: 16px;
`;

const FormSendButtonArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 12px;
  gap: 4px;
`;
